class CustomReorderPad {
  constructor(swat, lid, list, options = {}) {
    this.swat = swat;
    this.lid = lid;
    this.list = list;
    this.container = document.querySelector('[data-reorder-pad-container]');
    this.itemsContainer = this.container.querySelector('[data-reorder-pad-items]');
    this.paginationContainer = this.container.querySelector('[data-pagination]');
    this.loadMoreBtn = this.container.querySelector('[data-load-more]');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.customer = this.container.getAttribute('data-customer');
    this.wishlistRecommendedBadge = this.container.getAttribute('data-wishlist-recommended-badge');
    this.options = {
      itemsLimit: options.itemsLimit || 50
    };
    this.nextToken = null;
    this.isLoading = false;
    this.isRemoving = false;
    this.pendingRemoveItem = null;
    
    this.init();
  }
  
  createRemoveConfirmPopup() {
    if (this._removeConfirmPopup) return this._removeConfirmPopup;
    const wrapper = document.createElement('div');
    wrapper.id = 'reorder-pad-remove-confirm';
    wrapper.className = 'reorder-pad-remove-modal';
    wrapper.setAttribute('role', 'dialog');
    wrapper.setAttribute('aria-modal', 'true');
    wrapper.setAttribute('aria-labelledby', 'reorder-pad-remove-confirm-title');
    wrapper.innerHTML = `
      <div class="modal reorder-pad-remove-modal__overlay fixed top-0 left-0 w-full h-full flex items-center justify-center">
        <div class="modal__window relative bg-theme-bg text-theme-text text-center overflow-hidden has-motion" role="document">
          <button type="button" class="modal__close-btn absolute js-reorder-pad-confirm-close" aria-label="Close">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M14.714 5.28436L5.28595 14.7124M14.714 14.7124L5.28596 5.28436" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="modal__content">
            <h2 id="reorder-pad-remove-confirm-title" class="h4 reorder-pad-remove-confirm__title">Are you sure you want to remove this product from Reorder Pad?</h2>
            <div class="reorder-pad-remove-confirm__actions">
              <button type="button" class="btn btn--secondary js-reorder-pad-confirm-cancel">Cancel</button>
              <button type="button" class="btn btn--primary js-reorder-pad-confirm-remove">Remove</button>
            </div>
          </div>
        </div>
      </div>
    `;
    const overlay = wrapper.querySelector('.reorder-pad-remove-modal__overlay');
    const closeBtn = wrapper.querySelector('.js-reorder-pad-confirm-close');
    const cancelBtn = wrapper.querySelector('.js-reorder-pad-confirm-cancel');
    const removeBtn = wrapper.querySelector('.js-reorder-pad-confirm-remove');

    const hide = () => this.hideRemoveConfirm();
    overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });
    closeBtn.addEventListener('click', hide);
    cancelBtn.addEventListener('click', hide);
    removeBtn.addEventListener('click', () => {
      if (this.pendingRemoveItem) this.removeItem(this.pendingRemoveItem);
      hide();
    });
    wrapper.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') { e.preventDefault(); hide(); }
    });
    document.body.appendChild(wrapper);
    this._removeConfirmPopup = wrapper;
    return wrapper;
  }

  showRemoveConfirm(item) {
    this.pendingRemoveItem = item;
    const popup = this.createRemoveConfirmPopup();
    popup.setAttribute('open', '');
    document.body.classList.add('overflow-hidden');
    popup.querySelector('.js-reorder-pad-confirm-cancel').focus();
  }

  hideRemoveConfirm() {
    const popup = this._removeConfirmPopup;
    if (popup) {
      popup.removeAttribute('open');
      document.body.classList.remove('overflow-hidden');
    }
    this.pendingRemoveItem = null;
  }

  init() {
    if (this.container) this.createRemoveConfirmPopup();
    if (this.lid) {
      this.loadMoreBtn.addEventListener('click', () => this.loadMore());
      // Don't load items immediately - wait for metafield sync if needed
      // Items will be loaded after metafield products are synced
      // Show loading spinner using existing is-loading class
      this.itemsContainer.classList.add('is-loading');
    } else {
      this.renderEmptyState();
    }
  }
  
  /**
   * Initializes the list loading (called after metafield sync if needed)
   */
  initializeList() {
    // Remove loading state before loading items
    this.itemsContainer.classList.remove('is-loading');
    if (this.lid) {
      this.loadItems();
    } else {
      this.renderEmptyState();
    }
  }
  
  async loadItems() {
    if (this.nextToken === null) {
      this.itemsContainer.innerHTML = '';
    }
    
    const listData = {
      lid: this.lid,
      itemslimit: this.options.itemsLimit
    };

    if (this.nextToken !== null) {
      listData.next = this.nextToken;
    }

    if (this.customer === 'false') {
      if (this.list.listcontents.length === 0) {
        this.renderEmptyState();
        return;
      }
      this.renderProducts(this.list.listcontents);
    } else {
      try {      
        // If logged in, proceed with fetching the list
        await this.swat.wishlist.fetchPaginatedList(
          listData,
          (data) => {
            if (data.status === "error") {
              throw new Error("Error fetching list");
            }
            if (data.data.listcontents.length === 0) {
              this.renderEmptyState();
              return;
            }
            this.handleSuccess(data);
          },
          (error) => this.handleError(error)
        );
      } catch (error) {
        console.error('Error loading items:', error);
        this.handleError(error);
      }
    }
   
  }
  
  async handleSuccess(data) {
    if (!data || !data.data || !data.data.listcontents) {
      return;
    };
    
    this.renderProducts(data.data.listcontents);
    
    this.nextToken = data.pagination?.next || null;
    this.loadMoreBtn.style.display = this.nextToken ? 'block' : 'none';
  }

  async renderProducts(list) {
    if (this.isLoading) return;
    this.isLoading = true;
    this.itemsContainer.classList.add('is-loading');
    if (this.loadMoreBtn) {
      this.loadMoreBtn.classList.add('is-loading');
    }

    if(list.length === 0) {
      this.renderEmptyState();
    } else {
      try {
        // Process items in parallel using Promise.all
        const items = await Promise.all(
          list.map(async item => {
            try {
              const response = await fetch(window.location.origin + "/products/" + item.du.split('/products/')[1]?.split('?')[0] + '?variant=' + item.epi);
              if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
              const product = await response.text();
              const productHtml = new DOMParser().parseFromString(product, 'text/html');
              const price = productHtml.querySelector('.product-info__price .price__current');
              const comparePrice = productHtml.querySelector('.product-info__price .price__was');
              if (price) {
                item.price = price.textContent;
              }
              if (comparePrice) {
                item.comparePrice = comparePrice.textContent;
              }
              return item;
            } catch (error) {
              console.error(`Error fetching product ${item.du}:`, error);
              return item;
            }
          })
        );
        
        // Add all items to the container
        items.forEach((item) => {
          const itemElement = this.createItemElement(item);
          this.itemsContainer.appendChild(itemElement);
        });

      } catch (error) {
        console.error('Error processing wishlist items:', error);
        // Fallback to stored data
        list.forEach(item => {
          const itemElement = this.createItemElement(item);
          this.itemsContainer.appendChild(itemElement);
        });
      } finally {
        this.isLoading = false;
        this.itemsContainer.classList.remove('is-loading');
        if (this.loadMoreBtn) {
          this.loadMoreBtn.classList.remove('is-loading');
        }
      }
    }
  }

  renderEmptyState() {
    this.itemsContainer.innerHTML = `
    <div class="swym-storefront-layout-empty-container">
      <div class="swym-storefront-layout-empty-wishlist-container">
        <div class="swym-storefront-layout-empty-wishlist-content">
          <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="86" height="85" viewBox="0 0 86 85" fill="none">
            <rect width="59.5441" height="78.6833" transform="translate(0.710449 6.1748) rotate(-5.46887)" fill="white"></rect>
            <rect x="0.710938" y="6.1748" width="59.5441" height="78.6833" rx="1.21519" transform="rotate(-5.46887 0.710938 6.1748)" fill="#FDFDFD"></rect>
            <rect x="0.983626" y="6.39984" width="59.0441" height="78.1833" rx="0.965186" transform="rotate(-5.46887 0.983626 6.39984)" stroke="black" stroke-opacity="0.1" stroke-width="0.5"></rect>
            <rect x="5.57364" y="10.7446" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 5.57364 10.7446)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="13.0841" y="11.0938" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 13.0841 11.0938)" fill="#D0D0D0"></rect>
            <rect x="6.44223" y="19.8169" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 6.44223 19.8169)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="13.9527" y="20.166" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 13.9527 20.166)" fill="#D0D0D0"></rect>
            <rect x="7.31107" y="28.8892" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 7.31107 28.8892)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="14.8214" y="29.2383" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 14.8214 29.2383)" fill="#D0D0D0"></rect>
            <rect x="8.17942" y="37.9619" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 8.17942 37.9619)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="15.6899" y="38.311" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 15.6899 38.311)" fill="#D0D0D0"></rect>
            <rect x="9.04825" y="47.0342" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 9.04825 47.0342)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="16.5587" y="47.3833" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 16.5587 47.3833)" fill="#D0D0D0"></rect>
            <rect x="9.91666" y="56.1069" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 9.91666 56.1069)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="17.4271" y="56.4561" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 17.4271 56.4561)" fill="#D0D0D0"></rect>
            <rect x="10.7854" y="65.1792" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 10.7854 65.1792)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="18.2959" y="65.5283" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 18.2959 65.5283)" fill="#D0D0D0"></rect>
            <rect x="11.6538" y="74.2515" width="5.16454" height="5.16454" rx="0.455695" transform="rotate(-5.46887 11.6538 74.2515)" fill="#FBFBFB" stroke="#D0D0D0" stroke-width="0.303797"></rect>
            <rect x="19.1643" y="74.6006" width="44.6581" height="3.03797" rx="0.607593" transform="rotate(-5.46887 19.1643 74.6006)" fill="#D0D0D0"></rect>
            <rect x="55.0288" y="35.834" width="31.0853" height="31.0853" transform="rotate(13.2248 55.0288 35.834)" fill="url(#pattern0_489_1797)"></rect>
            <defs>
            <pattern id="pattern0_489_1797" patternContentUnits="objectBoundingBox" width="1" height="1">
            <use xlink:href="#image0_489_1797" transform="scale(0.0104167)"></use>
            </pattern>
            <image id="image0_489_1797" width="96" height="96" xlink:href="data:image/png;base64,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"></image>
            </defs>
          </svg>
          <div class="swym-storefront-layout-empty-wishlist-title">Your list is empty</div>
          <div class="swym-storefront-layout-empty-wishlist-description">Add more items to this list and see them here</div>
        </div>
      </div>
    </div>
  `;
  }
  
  handleError(error) {
    console.error('Error:', error);
    this.itemsContainer.innerHTML = `
      <div class="reorder-pad-error">
        <p>${error.message || 'An error occurred while loading your list.'}</p>
        <button class="button" onclick="window.location.reload()">Try Again</button>
      </div>
    `;
  }
  
  createItemElement(item) {
    if (!item.price) {
      item.price = `$${this.formatPrice(item.pr)}`;
    }

    // Check if product is recommended
    const isRecommended = item.cprops && item.cprops.isRecommended === true;

    const div = document.createElement('div');
    div.className = 'swym-storefront-layout-grid-item';
    div.setAttribute('data-item-id', item.id);
    div.innerHTML = `
      <a href="${item.du}" class="swym-storefront-layout-grid-item-image-container">
        <img src="${item.iu}" alt="${item.dt}" class="swym-storefront-layout-grid-item-image">
        ${isRecommended && this.wishlistRecommendedBadge ? `<span class="swym-storefront-layout-grid-item-recommended-badge">${this.wishlistRecommendedBadge}</span>` : ''}
      </a>
      <div class="swym-storefront-layout-grid-item-content">
        <a href="${item.du}" class="swym-storefront-layout-grid-item-title">${item.dt}</a>
        <div class="swym-storefront-layout-grid-item-price-variant">
          <span class="swym-storefront-layout-grid-item-final-price">${item.price}</span>
          ${item.comparePrice ? `<span class="swym-storefront-layout-grid-item-compare-price">${item.comparePrice}</span>` : ''}
          ${item.vi ? `<span class="swym-storefront-layout-price-devider">|</span><div class="swym-storefront-layout-grid-item-variants-container"><div class="swym-storefront-layout-variant">${item.vi}</div></div>` : ''}
        </div>
        <div class="swym-storefront-layout-grid-item-action-container">
          <button class="swym-storefront-layout-grid-item-add-to-cart-button" data-action="add-to-cart" aria-label="Add To Cart +">
            Add To Cart
          </button>
        </div>
      </div>
      
      <button type="button" class="swym-storefront-layout-grid-item-delete-button">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
          <path d="M14.714 5.28436L5.28595 14.7124M14.714 14.7124L5.28596 5.28436" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    `;
    
    // Add event listeners for buttons
    div.querySelector('.swym-storefront-layout-grid-item-add-to-cart-button')
      .addEventListener('click', () => this.addToCart(item));

    div.querySelector('.swym-storefront-layout-grid-item-delete-button')
      .addEventListener('click', () => this.showRemoveConfirm(item));
    
    return div;
  }
  
  formatPrice(price) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  }
  
  removeItem(item) {
    // Prevent multiple simultaneous removals
    if (this.isRemoving) {
      return;
    }

    if (!this.lid) {
      console.error('List ID not available');
      return;
    }

    // Set removing state
    this.isRemoving = true;

    let product = {
      epi: item.epi,
      empi: item.empi,
      du: item.du
    };

    // Check if product is recommended (has cprops.isRecommended)
    const isRecommended = item.cprops && item.cprops.isRecommended === true;
    
    // Extract product identifier for metafield removal
    let productIdentifier = null;
    if (isRecommended) {
      // Using wishlist metafield - use variant ID or product ID
      productIdentifier = {
        type: 'product',
        variant_id: item.epi,
        product_id: item.empi
      };
    }

    // Find the item element before deletion
    const itemElement = this.itemsContainer.querySelector(`[data-item-id="${item.id}"]`);
    
    // Disable all interactive elements while removing
    const allRemoveButtons = this.itemsContainer.querySelectorAll('.swym-storefront-layout-grid-item-delete-button');
    const allAddToCartButtons = this.itemsContainer.querySelectorAll('.swym-storefront-layout-grid-item-add-to-cart-button');
    
    allRemoveButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });
    
    allAddToCartButtons.forEach(btn => {
      btn.disabled = true;
      btn.style.pointerEvents = 'none';
      btn.style.opacity = '0.5';
    });
    
    // Add loading spinner to the item being removed
    if (itemElement) {
      itemElement.classList.add('is-loading');
      itemElement.style.pointerEvents = 'none';
    }
    
    // Use callbacks instead of await (Swym API pattern)
    this.swat.deleteFromList(
      this.lid,
      product,
      (response) => {
        // Success callback
        // Clear removing state
        this.isRemoving = false;
        
        // Re-enable all interactive elements
        allRemoveButtons.forEach(btn => {
          btn.disabled = false;
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        });
        
        allAddToCartButtons.forEach(btn => {
          btn.disabled = false;
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        });
        
        if (itemElement) {
          // Remove loading class
          itemElement.classList.remove('is-loading');
          
          // Add fade out animation
          itemElement.style.transition = 'opacity 0.3s ease';
          itemElement.style.opacity = '0';
          
          // Remove after animation
          setTimeout(() => {
            itemElement.remove();
            const remainingItems = this.itemsContainer.querySelectorAll(`[data-item-id]`);
            if(remainingItems.length === 0) {
              this.renderEmptyState();
            }
          }, 300);
        }
        
        // If product is recommended, remove it from metafield
        if (isRecommended && productIdentifier) {
          this.removeFromMetafield(productIdentifier);
        }
      },
      (error) => {
        // Error callback
        // Clear removing state
        this.isRemoving = false;
        
        // Re-enable all interactive elements
        allRemoveButtons.forEach(btn => {
          btn.disabled = false;
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        });
        
        allAddToCartButtons.forEach(btn => {
          btn.disabled = false;
          btn.style.pointerEvents = '';
          btn.style.opacity = '';
        });
        
        console.error('Error removing item:', error);
        
        if (itemElement) {
          // Remove loading class
          itemElement.classList.remove('is-loading');
          itemElement.style.pointerEvents = '';
          
          // Show visual feedback that deletion failed
          itemElement.style.border = '2px solid red';
          setTimeout(() => {
            itemElement.style.border = '';
          }, 2000);
        }
      }
    );
  }
  
  /**
   * Removes a product from the customer metafield using Accentuate
   * @param {Object} productIdentifier - Product identifier object with type and value
   *   - type: 'product' (for wishlist metafield) or 'handle' (for admin_wishlist metafield)
   *   - For 'product': variant_id and product_id
   *   - For 'handle': handle string
   */
  removeFromMetafield(productIdentifier) {
    if (!productIdentifier) return;
    
    // Wait for jQuery and Accentuate to be available
    const checkAndUpdate = () => {
      if (typeof jQuery === 'undefined' || typeof Accentuate === 'undefined') {
        setTimeout(checkAndUpdate, 100);
        return;
      }
      
      try {
        // Get customer ID from container
        const customerId = this.container?.getAttribute('data-customer-id');
        if (!customerId) {
          console.warn('Customer ID not found, cannot update metafield');
          return;
        }
        
        // Using wishlist metafield (full product data)
        const metafieldProductsScript = document.querySelector('[data-metafield-products]');
        if (!metafieldProductsScript) {
          console.warn('Metafield products script not found');
          return;
        }
        
        const currentProducts = JSON.parse(metafieldProductsScript.textContent) || [];
        
        // Verify the product exists in the metafield
        const productExists = currentProducts.some(p => {
          if (p.variant_id && productIdentifier.variant_id) {
            return parseInt(p.variant_id, 10) === parseInt(productIdentifier.variant_id, 10);
          }
          if (p.id && productIdentifier.product_id) {
            return parseInt(p.id, 10) === parseInt(productIdentifier.product_id, 10);
          }
          return false;
        });

        
        if (!productExists) {
          return;
        }
        
        // Update the metafield using Accentuate
        // Create a temporary form for Accentuate to process
        const form = document.createElement('form');
        form.id = 'accentuate-metafield-update-' + Date.now();
        form.method = 'POST';
        form.style.display = 'none';
        
        const customerIdInput = document.createElement('input');
        customerIdInput.type = 'hidden';
        customerIdInput.name = 'customer[id]';
        customerIdInput.value = customerId;
        form.appendChild(customerIdInput);
        
        // Use Accentuate's options.remove to remove a specific value from repeatable field
        // See: https://help.accentuate.io/liquid-guides/allow-customers-to-change-their-field-values
        
        // Add the remove option
        const removeOption = document.createElement('input');
        removeOption.type = 'hidden';
        removeOption.name = 'options.remove';
        removeOption.value = 'true';
        form.appendChild(removeOption);
        
        // Submit the product reference to remove
        const productGid = `gid://shopify/Product/${productIdentifier.product_id}`;
        const metafieldInput = document.createElement('input');
        metafieldInput.type = 'hidden';
        metafieldInput.name = 'metafield[accentuate.recommended_reorder_pad]';
        metafieldInput.value = productGid;
        form.appendChild(metafieldInput);
        
        document.body.appendChild(form);
        
        // Use Accentuate to submit the form
        const $form = jQuery(form);
        Accentuate($form);
        
        // Listen for success/error
        $form.on('accentuate:success', () => {
          // Update the script tag - remove the deleted product from the array
          const currentProducts = JSON.parse(metafieldProductsScript.textContent) || [];
          const updatedProducts = currentProducts.filter(p => {
            if (p.variant_id && productIdentifier.variant_id) {
              return parseInt(p.variant_id, 10) !== parseInt(productIdentifier.variant_id, 10);
            }
            if (p.id && productIdentifier.product_id) {
              return parseInt(p.id, 10) !== parseInt(productIdentifier.product_id, 10);
            }
            return true;
          });
          metafieldProductsScript.textContent = JSON.stringify(updatedProducts);
          // Clean up form
          if (form.parentNode) {
            form.parentNode.removeChild(form);
          }
        });
        
        $form.on('accentuate:error', (error) => {
          console.error('Error updating metafield:', error);
          console.error('Form data:', {
            metafieldName: productIdentifier.type === 'product' ? 'metafield[accentuate.recommended_reorder_pad]' : metafieldName,
            updatedValue: productIdentifier.type === 'product' ? JSON.stringify(updatedValue) : updatedValue,
            customerId: customerId
          });
          // Clean up form
          if (form.parentNode) {
            form.parentNode.removeChild(form);
          }
        });
        
        // Submit the form (Accentuate will intercept it)
        $form.submit();
        
      } catch (error) {
        console.error('Error removing product from metafield:', error);
      }
    };
    
    checkAndUpdate();
  }
  
  async addToCart(item) {
    // Prevent adding to cart while removal is in progress
    if (this.isRemoving) {
      return;
    }

    try {
      const formData = {
        items: [{
          id: item.epi,
          quantity: 1
        }]
      };

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(formData)
      };

      const response = await fetch(`${theme.routes.cartAdd}`, fetchRequestOpts);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || 'Failed to add item to cart');
      }

      // Remove item from wishlist using existing function
      await this.removeItem(item);

      if (theme.settings.afterAtc === 'drawer') {
        // Open cart drawer
        this.cartDrawer.open();
        const cartItems = document.querySelector('cart-items');
        const cartItemsAll = document.querySelectorAll('.cart-item');
        if (cartItems && cartItemsAll.length > 0) {
          cartItems.refresh();
        } else {
          const response = this.cartDrawer.getSectionsToRender().map((section) => section.section);
          const cartResponse = await fetch(`?sections=${response.join(',')}`);
          const sections = await cartResponse.json();
          this.cartDrawer.renderContents({ sections }, false);
        }
      } else {
        setTimeout(() => {
          window.location.href = theme.routes.cart;
        }, 300);
      }

      
    } catch (error) {
      console.error('Error adding to cart:', error);
      // Optional: Show error message to user
    }
  }

  loadMore() {
    if (this.nextToken) {
      this.loadItems();
    }
  }


  /**
   * Syncs products from metafield to Swym wishlist
   * @param {Array} metafieldProducts - Array of product objects from metafield
   */
  async syncMetafieldProducts(metafieldProducts) {
    if (!this.lid || !metafieldProducts || metafieldProducts.length === 0) {
      return;
    }

    try {
      let currentListItems = [];
      
      if (this.list && this.list.listcontents && Array.isArray(this.list.listcontents)) {
        currentListItems = this.list.listcontents;
      } else {
        currentListItems = [];
      }

      // Create set of existing variant IDs
      const existingVariantIds = new Set(
        currentListItems.map(item => item.epi)
      );

      // Filter products that aren't in the list and have valid data
      const productsToAdd = metafieldProducts.filter(product => {
        // Check if product has required data (variant_id must not be null)
        if (!product.variant_id || product.variant_id === null || !product.id || !product.url) {
          return false;
        }
        
        // Check if product variant is already in the list
        const variantId = parseInt(product.variant_id, 10);
        const notInList = !existingVariantIds.has(variantId);
        
        return notInList;
      });

      if (productsToAdd.length === 0) {
        return;
      }


      // Prepare products array for Swym API
      const swymProducts = [];
      for (const product of productsToAdd) {
        // Ensure variant_id and product id are valid numbers
        if (!product.variant_id || !product.id) {
          console.warn('Skipping product with missing IDs:', product);
          continue;
        }

        // Swym API expects: epi (variant id), empi (product id), du (product url)
        // Ensure URL is full URL if it's relative
        let productUrl = product.url || '';
        if (productUrl && !productUrl.startsWith('http')) {
          productUrl = window.location.origin + productUrl;
        }
        
        const swymProduct = {
          epi: parseInt(product.variant_id, 10),
          empi: parseInt(product.id, 10),
          du: productUrl,
          cprops: {
            isRecommended: true
          }
        };

        // Validate product data
        if (!swymProduct.epi || !swymProduct.empi || !swymProduct.du) {
          console.warn('Skipping product with invalid data:', swymProduct);
          continue;
        }

        swymProducts.push(swymProduct);
      }

      if (swymProducts.length === 0) {
        return;
      }

      // Add products in batches of 10 (Swym API limit)
      const batchSize = 10;
      for (let i = 0; i < swymProducts.length; i += batchSize) {
        const batch = swymProducts.slice(i, i + batchSize);
        
        await new Promise((resolve) => {
          try {            
            // Use addProductsToList method (up to 10 products per request)
            this.swat.addProductsToList(
              this.lid,
              batch,
              (response) => {
                resolve(response);
              },
              (error) => {
                console.error('Error adding products to wishlist:', error);
                // Continue even if there's an error
                resolve();
              }
            );
          } catch (error) {
            console.error('Exception calling Swym addProductsToList:', error);
            resolve();
          }
        });

        // Small delay between batches to avoid rate limiting
        if (i + batchSize < swymProducts.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      }

      // Products have been added, but don't reload here
      // The list will be loaded after sync completes
    } catch (error) {
      console.error('Error syncing metafield products:', error);
    }
  }
}

function onSwymLoadCallback(swat) {
  if (swat) {
    let lid = null; // List ID 
    let reorderList = null;
  
    let onSuccess = async function(lists) {
      if (lists.length > 0) {
        reorderList = lists.find(list => list.lname === 'Reorder Pad');
        lid = reorderList?.lid;
      }
      
      let reorderPad;
      if (lid) {
        // Initialize reorder pad with the first list's ID
        reorderPad = new CustomReorderPad(swat, lid, reorderList, {
          itemsLimit: 50
        });
      } else {
        reorderPad = new CustomReorderPad(swat, null, null, {
          itemsLimit: 50
        });
        let listConfig = {
          "lname": "Reorder Pad", // unique list name.
        };
        await new Promise((resolve) => {
          swat.createList(
            listConfig,
            (list) => {
              if (list && list.lid) {
                lid = list.lid;
                reorderPad.lid = lid;
                reorderPad.list = list;
              }
              resolve();
            },
            (error) => {
              console.log('Error while creating reorder pad', error);
              resolve();
            }
          );
        });
      }

      // Fetch and sync metafield products to wishlist BEFORE rendering
      const metafieldProductsScript = document.querySelector('[data-metafield-products]');
      if (metafieldProductsScript && lid) {
        try {
          const products = JSON.parse(metafieldProductsScript.textContent);
          if (products && products.length > 0) {
            // Wait a bit for the list to be ready, then sync products
            const waitTime = reorderList ? 500 : 1000;
            setTimeout(async () => {
              try {
                // Use product data directly from wishlist metafield
                await reorderPad.syncMetafieldProducts(products);
                // Now initialize and render the list (only once)
                reorderPad.initializeList();
              } catch (error) {
                console.error('Error during metafield sync:', error);
                // On error, still initialize the list
                reorderPad.initializeList();
              }
            }, waitTime);
          } else {
            // No metafield products, just initialize the list
            reorderPad.initializeList();
          }
        } catch (error) {
          console.error('Error parsing metafield products:', error);
          // On error, still initialize the list
          reorderPad.initializeList();
        }
      } else {
        // No metafield products to sync, just initialize the list
        reorderPad.initializeList();
      }
    }
    
    let onError = function(error) {
      console.log("Error while fetching all Lists", error);
    }
    
    // Call `fetchLists` function
    swat.fetchLists({
      callbackFn: onSuccess,
      errorFn: onError
    });
  }
};

if (!window.SwymCallbacks) {
  window.SwymCallbacks = [];
}
window.SwymCallbacks.push(onSwymLoadCallback);
